import React, { useEffect, useState } from "react";
import { lpTrack } from "../../helpers/lpTrack";
import { scrollToForm, useScrolled } from "../../helpers/paidScroll";
import { fixStrapiUrl } from "../../helpers/strapiUrl";

/**
 * Page-scoped sections + chrome for the AXA paid LP (redesign brief). Every
 * piece here is NEW and rendered only on the allowlisted LP by masterTemplate —
 * no shared/site-wide component is modified, and the hero lead form is reused
 * untouched. Brand colors only (--primary-color = #00008F on AXA).
 */

const PHONE_LABEL = "+34 911 982 326";
const PHONE_TEL = "tel:+34911982326";
const FORM_ANCHOR = "#calculadora-life5"; // hero calculator anchor (no-JS fallback)

/** Anchor onClick: smooth-scroll to the hero form + pulse it, then no-op nav. */
const onCtaClick =
  (location: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    scrollToForm(location);
  };

const PhoneGlyph = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/**
 * Keyframes for the LP-only animations (aurora hero glow, scroll-pulse on the
 * form, the promo dot ping). Injected once; frozen under prefers-reduced-motion.
 */
export const PaidStyles = () => (
  <style
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: `
@keyframes axaAuroraDrift {
  0%   { transform: translate3d(-6%, -4%, 0) rotate(0deg); }
  50%  { transform: translate3d(6%, 4%, 0) rotate(6deg); }
  100% { transform: translate3d(-6%, -4%, 0) rotate(0deg); }
}
@keyframes axaBreathe { 0%, 100% { opacity: .5; } 50% { opacity: .95; } }
/* Pulse via outline (not box-shadow): the form card's Tailwind shadow compiles
   with !important, and per the cascade !important author rules beat animations —
   an animated box-shadow would never show. Outline has no such conflict. */
@keyframes axaPulseRing {
  0%   { outline-color: rgba(0,0,143,.55); outline-offset: 0px; }
  70%  { outline-color: rgba(0,0,143,0);   outline-offset: 12px; }
  100% { outline-color: rgba(0,0,143,0);   outline-offset: 0px; }
}
@keyframes axaPing { 75%, 100% { transform: scale(2.3); opacity: 0; } }
.axa-aurora { animation: axaAuroraDrift 26s ease-in-out infinite, axaBreathe 12s ease-in-out infinite; }
.axa-pulse  { outline: 3px solid rgba(0,0,143,.55); border-radius: 20px; animation: axaPulseRing 1.3s ease-out 1; }
.axa-ping   { animation: axaPing 1.6s cubic-bezier(0,0,.2,1) infinite; }
@media (prefers-reduced-motion: reduce) {
  .axa-aurora, .axa-ping { animation: none !important; }
  .axa-pulse { animation: none !important; }
}`,
    }}
  />
);

/** Section 1a — top promo bar in normal flow (scrolls away with the page). */
export const PromoBar = () => (
  <div className="bg-[#FAB71D] text-[var(--primary-color)]">
    <div className="max-w-content mx-auto px-[24px] py-[8px] flex items-center justify-center gap-[12px] text-center">
      <span className="relative flex h-[8px] w-[8px] shrink-0" aria-hidden="true">
        <span className="axa-ping absolute inline-flex h-full w-full rounded-full bg-[var(--primary-color)] opacity-75" />
        <span className="relative inline-flex h-[8px] w-[8px] rounded-full bg-[var(--primary-color)]" />
      </span>
      <span className="font-DM text-[12px] md:text-[14px] leading-tight">
        <strong className="font-bold">¡Oferta! 1€/mes</strong> los 3 primeros
        meses — solo nuevos clientes
      </span>
      <a
        href={FORM_ANCHOR}
        onClick={onCtaClick("promo_bar")}
        className="hidden sm:inline-flex shrink-0 items-center gap-[6px] rounded-full bg-[var(--primary-color)] px-[14px] py-[6px] font-DM font-bold text-[13px] text-white no-underline transition-opacity hover:opacity-90"
      >
        Calcular precio <span aria-hidden="true">→</span>
      </a>
    </div>
  </div>
);

/** Section 1b — floating offer FAB; appears once scrolled past 80px. */
export const FloatingFab = () => {
  const scrolled = useScrolled(80);
  return (
    <button
      type="button"
      onClick={() => scrollToForm("fab")}
      aria-label="Calcular mi precio"
      className={`fixed bottom-[20px] left-1/2 z-50 flex items-center gap-[10px] rounded-full border border-white/25 bg-[rgba(0,0,143,0.82)] backdrop-blur-md py-[8px] pl-[8px] pr-[18px] text-white shadow-[0_14px_40px_-8px_rgba(0,0,143,0.5)] transition-all duration-300 active:scale-[0.97] ${
        scrolled
          ? "opacity-100 -translate-x-1/2 translate-y-0 pointer-events-auto"
          : "opacity-0 -translate-x-1/2 translate-y-[16px] pointer-events-none"
      }`}
    >
      <span className="flex h-[36px] w-[36px] items-center justify-center rounded-full bg-white font-DM font-bold text-[14px] text-[var(--primary-color)]">
        1€
      </span>
      <span className="flex flex-col items-start leading-tight text-left">
        <span className="font-DM text-[11px] text-[#FAB71D] whitespace-nowrap">
          Oferta · 1€/mes · 3 meses
        </span>
        <span className="font-DM font-bold text-[14px] whitespace-nowrap">
          Calcular mi precio →
        </span>
      </span>
    </button>
  );
};

/** Section 4 — compact trust strip on a light background. */
export const TrustBar = () => (
  <section className="bg-[#F5F7FA]">
    <div className="max-w-content mx-auto px-[24px] py-[14px] flex flex-wrap items-center justify-center gap-x-[10px] gap-y-[4px] text-center font-DM text-[13px] md:text-[14px] text-[#424242]">
      <span aria-hidden="true" className="text-[#FAB71D]">
        ★★★★★
      </span>
      <span>
        +50.000 familias protegidas · Gratis y sin compromiso · Respaldado por{" "}
        <strong className="font-bold text-[var(--primary-color)]">AXA</strong>
      </span>
    </div>
  </section>
);

/** Reusable centered CTA strip; matches the bg of the section above it so it
 *  reads as part of it. Used after Benefits and after the comparison table. */
export const SectionCTA = ({
  label,
  location,
  bg = "#FFFFFF",
}: {
  label: string;
  location: string;
  bg?: string;
}) => (
  <section style={{ backgroundColor: bg }}>
    <div className="max-w-content mx-auto px-[24px] -mt-[24px] md:-mt-[32px] pb-[56px] md:pb-[72px] flex justify-center">
      <a
        href={FORM_ANCHOR}
        onClick={onCtaClick(location)}
        className="inline-flex items-center gap-[8px] rounded-full bg-[var(--primary-color)] px-[28px] py-[14px] font-DM font-medium text-white text-[18px] no-underline transition-opacity hover:opacity-90 active:scale-[0.985]"
      >
        {label}
        <span aria-hidden="true">→</span>
      </a>
    </div>
  </section>
);

/* ----------------------------- Trustpilot (§7) ---------------------------- */
/* NOTE: placeholder/mock reviews and rating from the brief — to be replaced
   with real Trustpilot data/widget before this is treated as final copy. */

interface Review {
  stars: number;
  title: string;
  body: string;
  name: string;
  place: string;
  date: string;
}

const REVIEWS: Review[] = [
  {
    stars: 5,
    title: "Súper rápido y fácil",
    body: "Calculé mi precio en dos minutos y contraté sin nada de papeleo. Todo online y clarísimo.",
    name: "Marta G.",
    place: "Madrid",
    date: "hace 3 días",
  },
  {
    stars: 5,
    title: "Mejor de lo que esperaba",
    body: "Pensaba que sería un lío y fue justo lo contrario. Sin exámenes médicos y a un precio muy competitivo.",
    name: "Javier R.",
    place: "Barcelona",
    date: "hace 1 semana",
  },
  {
    stars: 5,
    title: "Cobertura AXA al mejor precio",
    body: "La tranquilidad de tener a AXA detrás con un precio que no encontré en ningún otro sitio. Recomendado.",
    name: "Laura P.",
    place: "Valencia",
    date: "hace 2 semanas",
  },
  {
    stars: 5,
    title: "Por fin un seguro sin trampas",
    body: "Sin permanencia y sin letra pequeña. Me explicaron todo y contraté con total confianza.",
    name: "David M.",
    place: "Sevilla",
    date: "hace 3 semanas",
  },
  {
    stars: 5,
    title: "Lo más sencillo del mundo",
    body: "El proceso más simple que he hecho online. En un rato tenía el seguro activo.",
    name: "Carmen V.",
    place: "Bilbao",
    date: "hace 1 mes",
  },
  {
    stars: 4,
    title: "Buen seguro, buen precio",
    body: "Contenta con la cobertura y el precio. Una llamada rápida me resolvió las dudas que tenía.",
    name: "Ana T.",
    place: "Málaga",
    date: "hace 1 mes",
  },
];

const StarTile = ({ filled }: { filled: boolean }) => (
  <span
    className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-[2px]"
    style={{ backgroundColor: filled ? "#00B67A" : "#DCDCE6" }}
  >
    <svg viewBox="0 0 24 24" width="12" height="12" fill="#FFFFFF" aria-hidden="true">
      <path d="M12 2l2.95 6.18 6.8.78-5.02 4.62 1.36 6.69L12 17.77 5.91 20.27l1.36-6.69L2.25 8.96l6.8-.78L12 2z" />
    </svg>
  </span>
);

const StarRow = ({ n }: { n: number }) => (
  <span className="inline-flex gap-[3px]" aria-label={`${n} de 5 estrellas`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <StarTile key={i} filled={i < n} />
    ))}
  </span>
);

const TrustpilotMark = () => (
  <span className="inline-flex items-center gap-[6px]">
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="#00B67A" d="M12 2l2.95 6.18 6.8.78-5.02 4.62 1.36 6.69L12 17.77 5.91 20.27l1.36-6.69L2.25 8.96l6.8-.78L12 2z" />
    </svg>
    <span className="font-DM font-bold text-[15px] text-[#424242]">Trustpilot</span>
  </span>
);

const ReviewCard = ({ review }: { review: Review }) => (
  <article className="flex-1 basis-0 min-w-0 flex flex-col gap-[12px] rounded-[16px] border border-[#EDEDED] bg-white p-[24px] shadow-[0_2px_12px_-6px_rgba(0,0,0,0.15)]">
    <StarRow n={review.stars} />
    <h3 className="font-DM font-bold text-[#263137] text-[17px] leading-tight">
      {review.title}
    </h3>
    <p className="font-DM text-[15px] leading-[1.5] text-[#555555] flex-1">
      {review.body}
    </p>
    <div className="font-DM text-[13px] text-[#686868]">
      <span className="font-medium text-[#424242]">{review.name}</span> ·{" "}
      {review.place} · {review.date}
    </div>
    <div className="font-DM text-[12px] text-[#00B67A]">
      Opinión verificada · Trustpilot
    </div>
  </article>
);

const Arrow = ({ dir }: { dir: "prev" | "next" }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === "prev" ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
  </svg>
);

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/** Section 7 — Trustpilot reviews carousel (3/page desktop, 1/page mobile). */
export const TrustpilotReviews = () => {
  const [perPage, setPerPage] = useState(1);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const calc = () => setPerPage(window.innerWidth >= 768 ? 3 : 1);
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const pages = chunk(REVIEWS, perPage);
  const pageCount = pages.length;

  // Keep the active page in range when the layout (perPage) changes.
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount - 1));
  }, [pageCount]);

  // Auto-rotate every 5s unless paused (hover/focus) or there's a single page.
  useEffect(() => {
    if (paused || pageCount <= 1) return;
    const id = window.setInterval(
      () => setPage((p) => (p + 1) % pageCount),
      5000
    );
    return () => window.clearInterval(id);
  }, [paused, pageCount]);

  const go = (next: number) => setPage((next + pageCount) % pageCount);

  return (
    <section className="bg-[#F5F7FA]">
      <div className="max-w-content mx-auto px-[24px] py-[48px] md:py-[64px]">
        <div className="flex flex-col items-center gap-[8px] text-center mb-[32px]">
          <StarRow n={5} />
          <p className="font-DM text-[15px] text-[#424242]">
            <strong className="font-bold">4,8/5</strong> · Excelente · 3.241
            opiniones
          </p>
          <TrustpilotMark />
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
          role="group"
          aria-roledescription="carrusel"
          aria-label="Opiniones de clientes"
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${page * 100}%)` }}
            >
              {pages.map((group, gi) => (
                <div key={gi} className="min-w-full flex gap-[24px]">
                  {group.map((r, ri) => (
                    <ReviewCard key={`${gi}-${ri}`} review={r} />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-[24px] flex items-center justify-center gap-[16px]">
            <button
              type="button"
              onClick={() => go(page - 1)}
              aria-label="Anterior"
              className="flex h-[40px] w-[40px] items-center justify-center rounded-full border border-[#E2E2E2] bg-white text-[var(--primary-color)] transition-colors hover:bg-[#ECF0FF]"
            >
              <Arrow dir="prev" />
            </button>
            <div className="flex items-center gap-[8px]">
              {pages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  aria-label={`Ir a la página ${i + 1}`}
                  aria-current={i === page}
                  className={`h-[8px] rounded-full transition-all ${
                    i === page
                      ? "w-[24px] bg-[var(--primary-color)]"
                      : "w-[8px] bg-[#C8CBE0]"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => go(page + 1)}
              aria-label="Siguiente"
              className="flex h-[40px] w-[40px] items-center justify-center rounded-full border border-[#E2E2E2] bg-white text-[var(--primary-color)] transition-colors hover:bg-[#ECF0FF]"
            >
              <Arrow dir="next" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ----------------------------- Press logos (§9) --------------------------- */

interface IconItem {
  SingleImage?: { url?: string; alternativeText?: string };
  ImageURL?: string;
  AlternativeText?: string;
}

/**
 * Section 9 — press logos. Keeps the SAME logo images as the original IconsRow
 * block (reuses its `IconsList` data verbatim); only the styling changes:
 * a "Recomendado por:" eyebrow + muted/grayscale logos on a clean surface.
 */
export const PressLogos = ({ data }: { data?: { IconsList?: IconItem[] } }) => {
  const items = (data?.IconsList || [])
    .map((icon) => {
      const strapiUrl = icon?.SingleImage?.url
        ? fixStrapiUrl(icon.SingleImage.url)
        : null;
      const url = strapiUrl || icon?.ImageURL;
      const alt =
        icon?.SingleImage?.alternativeText || icon?.AlternativeText || "";
      return url ? { url, alt } : null;
    })
    .filter(Boolean) as { url: string; alt: string }[];

  if (items.length === 0) return null;

  return (
    <section className="bg-white border-t border-[#EDEDED]">
      <div className="max-w-content mx-auto px-[24px] py-[40px] md:py-[48px] flex flex-col items-center gap-[24px]">
        <span className="font-DM text-[12px] md:text-[13px] uppercase tracking-[0.08em] text-[#686868]">
          Recomendado por:
        </span>
        <div className="flex flex-wrap items-center justify-center gap-x-[36px] md:gap-x-[48px] gap-y-[24px]">
          {items.map((it, i) => (
            <img
              key={i}
              loading="lazy"
              src={it.url}
              alt={it.alt}
              className="h-[26px] md:h-[30px] w-auto object-contain opacity-60 grayscale transition duration-200 hover:opacity-100 hover:grayscale-0"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/** Section 10 — "prefer a call?" block, mobile only. */
export const MobilePhoneBlock = () => (
  <section className="md:hidden bg-[#ECF0FF] border-t border-neutral-200">
    <a
      href={PHONE_TEL}
      onClick={() => lpTrack("phone_click", { location: "mobile_section" })}
      className="max-w-content mx-auto px-[24px] py-[20px] flex items-center justify-center gap-[12px] text-[var(--primary-color)] no-underline"
    >
      <PhoneGlyph className="shrink-0" />
      <span className="flex flex-col leading-tight">
        <span className="font-DM text-[13px] text-[#424242]">
          ¿Prefieres que te llamemos?
        </span>
        <span className="font-DM font-bold text-[20px]">{PHONE_LABEL}</span>
      </span>
    </a>
  </section>
);

/** Section 11 — closing CTA on brand blue; button smooth-scrolls to the form. */
export const FinalCTA = () => (
  <section className="bg-[var(--primary-color)]">
    <div className="max-w-content mx-auto px-[24px] py-[48px] md:py-[56px] flex flex-col md:flex-row md:items-center md:justify-between gap-[24px]">
      <div className="text-center md:text-left">
        <p className="font-DM font-bold text-white text-[26px] md:text-[32px] leading-tight">
          Calcula tu seguro en 2 minutos
        </p>
        <p className="mt-[8px] font-DM text-white/75 text-[15px] md:text-[17px]">
          Gratis · Sin compromiso · Sin tarjeta
        </p>
      </div>
      <div className="flex flex-col md:flex-row md:items-center gap-[16px] shrink-0">
        <a
          href={FORM_ANCHOR}
          onClick={onCtaClick("final_cta")}
          className="inline-flex items-center justify-center gap-[8px] rounded-full bg-white px-[28px] py-[14px] font-DM font-bold text-[var(--primary-color)] text-[16px] no-underline w-full md:w-auto"
        >
          Calcular mi precio
          <span aria-hidden="true">→</span>
        </a>
        <a
          href={PHONE_TEL}
          onClick={() => lpTrack("phone_click", { location: "final_cta" })}
          className="hidden md:flex items-center gap-[10px] rounded-[16px] border border-white/30 px-[18px] py-[12px] text-white no-underline transition-colors hover:bg-white/10"
        >
          <PhoneGlyph className="shrink-0" />
          <span className="flex flex-col leading-tight">
            <span className="font-DM font-bold text-[18px]">{PHONE_LABEL}</span>
            <span className="font-DM text-white/60 text-[12px]">
              Lun–Vie · Sin compromiso
            </span>
          </span>
        </a>
      </div>
    </div>
  </section>
);
